import { useEffect, useState } from "react";
import { api, local } from "@/lib/api";
import { PageHeader, Tabs } from "@/components/kit";
import { BadgeShareDialog } from "@/components/BadgeShareDialog";
import { CHALLENGES, BADGE_CATEGORIES, BADGE_CATEGORY_LABELS, CATEGORY_COLORS, CHALLENGE_TYPE_COLORS, CHALLENGE_CATEGORIES } from "@/data/achievements";

export default function Achievements() {
  const [progress, setProgress] = useState(null);
  const [badges, setBadges] = useState([]);
  const [view, setView] = useState("badges");
  const [cat, setCat] = useState("All");
  const [ccat, setCcat] = useState("All");
  const [sharing, setSharing] = useState(null);
  const [doneCh, setDoneCh] = useState(local.get("completed_challenges", {}));

  useEffect(() => {
    api.get("/gamification/progress").then((r) => setProgress(r.data)).catch(() => {});
    api.get("/gamification/badges").then((r) => setBadges(r.data)).catch(() => {});
  }, []);

  const toggleCh = (id) => { const n = { ...doneCh, [id]: !doneCh[id] }; setDoneCh(n); local.set("completed_challenges", n); };
  const p = progress || { level: 1, xp: 0, xp_to_next_level: 100, current_streak: 0, total_checkins: 0 };
  const shown = badges.filter((b) => cat === "All" || (cat === "Earned" ? b.earned : b.category === cat));
  const chShown = CHALLENGES.filter((c) => ccat === "All" || c.type === ccat.toLowerCase());
  const pct = Math.min(100, (p.xp / (p.xp + p.xp_to_next_level || 1)) * 100);

  return (
    <div data-testid="achievements-page">
      <PageHeader title="Badges & Progress" subtitle="Celebrate every small step" />
      <div className="mm-gradient rounded-3xl p-6 sm:p-8 mm-fade-up">
        <div className="flex justify-between items-end">
          <div><div className="text-sm opacity-80">Level</div><div className="font-display text-5xl font-extrabold" data-testid="achievements-level">{p.level}</div></div>
          <div className="text-right text-sm opacity-90"><div data-testid="achievements-xp">{p.xp} XP</div><div>{p.xp_to_next_level} XP to next level</div></div>
        </div>
        <div className="h-2.5 rounded-full bg-white/25 mt-4"><div className="h-full rounded-full bg-white transition-all" style={{ width: `${pct}%` }} /></div>
        <div className="grid grid-cols-3 gap-3 mt-6 text-center">
          {[["🔥", p.current_streak, "Streak"], ["📋", p.total_checkins, "Check-ins"], ["🏅", badges.filter((b) => b.earned).length, "Badges"]].map(([e, v, l]) => (
            <div key={l} className="bg-white/15 rounded-2xl py-3"><div>{e}</div><div className="font-bold text-xl">{v}</div><div className="text-xs opacity-80">{l}</div></div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mt-8 mb-4">
        {[["badges", "Badges"], ["challenges", "Challenges"]].map(([id, l]) => (
          <button key={id} onClick={() => setView(id)} className={`mm-chip ${view === id ? "mm-chip-on" : ""}`} data-testid={`achievements-view-${id}`}>{l}</button>
        ))}
      </div>

      {view === "badges" ? (
        <>
          <Tabs items={BADGE_CATEGORIES.map((c) => ({ id: c, name: BADGE_CATEGORY_LABELS[c] }))} value={cat} onChange={setCat} testPrefix="badge-filter" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mm-stagger">
            {shown.map((b) => (
              <div key={b.id} className={`mm-card p-5 text-center ${b.earned ? "" : "opacity-50 grayscale"}`} data-testid={`badge-${b.id}`}>
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-3xl" style={{ background: `${b.color || CATEGORY_COLORS[b.category]}22` }}>{b.icon}</div>
                <div className="font-semibold mt-3 text-sm">{b.name}</div>
                <div className="text-xs text-mm-muted mt-1">{b.description}</div>
                {b.earned && <div className="text-xs font-bold text-mm-success mt-2">Earned ✓</div>}
                {b.earned && <button className="mm-chip !py-1 !px-2.5 !text-[11px] mt-2" onClick={() => setSharing(b)} data-testid={`badge-share-${b.id}`}>Share</button>}
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <Tabs items={CHALLENGE_CATEGORIES.map((c) => ({ id: c, name: c }))} value={ccat} onChange={setCcat} testPrefix="challenge-filter" />
          <div className="grid sm:grid-cols-2 gap-3">
            {chShown.map((c) => (
              <button key={c.id} onClick={() => toggleCh(c.id)} data-testid={`challenge-${c.id}`} className={`mm-card p-4 text-left flex gap-4 items-center ${doneCh[c.id] ? "!border-mm-success" : ""}`}>
                <span className="text-3xl">{c.emoji}</span>
                <div className="flex-1">
                  <div className="font-semibold">{c.title} {doneCh[c.id] && "✅"}</div>
                  <div className="text-xs text-mm-muted">{c.description}</div>
                </div>
                <span className="text-xs font-bold px-2 py-1 rounded-full text-white" style={{ background: CHALLENGE_TYPE_COLORS[c.type] }}>+{c.points}</span>
              </button>
            ))}
          </div>
        </>
      )}
      {sharing && <BadgeShareDialog badge={sharing} onClose={() => setSharing(null)} />}
    </div>
  );
}
